import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Skeleton,
} from '@mui/material'
import { Close, Download } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Form, required, useNotify, useTranslate } from 'react-admin'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'
import { routeConfigServices } from 'src/services/reservation/route-config/routeConfig.service'
import { workUnitPaymentServices } from 'src/services/payment/workUnit/WorkUnit.service'
import { productTypeServices } from 'src/services/reservation/product-type/product_type.service'

export const RouteConfigExportButton = () => {
  const translate = useTranslate()
  const notify = useNotify()

  const [openModal, setOpenModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [workUnitOptions, setWorkUnitOptions] = useState([])
  const [loadingWorkUnits, setLoadingWorkUnits] = useState(false)
  const [productTypeOptions, setProductTypeOptions] = useState([])
  const [loadingProductTypes, setLoadingProductTypes] = useState(false)

  const hasWorkUnits = workUnitOptions.length > 0
  const hasProductTypes = productTypeOptions.length > 0

  const onOpenModal = () => {
    setOpenModal(true)
  }

  const onCloseModal = () => {
    setOpenModal(false)
  }

  const fetchWorkUnits = useCallback(async () => {
    try {
      setLoadingWorkUnits(true)
      const response = await workUnitPaymentServices.getByAll()
      setWorkUnitOptions(response || [])
    } catch (error) {
      setWorkUnitOptions([])
      notify('ra.message.route_config_work_units_error', { type: 'error' })
    } finally {
      setLoadingWorkUnits(false)
    }
  }, [notify])

  const fetchProductTypes = useCallback(async () => {
    try {
      setLoadingProductTypes(true)
      const response = await productTypeServices.getByParam()
      setProductTypeOptions(response || [])
    } catch (error) {
      setProductTypeOptions([])
      notify('ra.message.route_config_product_types_error', { type: 'error' })
    } finally {
      setLoadingProductTypes(false)
    }
  }, [notify])

  useEffect(() => {
    if (openModal && !hasWorkUnits && !loadingWorkUnits) {
      fetchWorkUnits()
    }
    if (openModal && !hasProductTypes && !loadingProductTypes) {
      fetchProductTypes()
    }
  }, [fetchProductTypes, fetchWorkUnits, hasProductTypes, hasWorkUnits, loadingProductTypes, loadingWorkUnits, openModal])

  const handleExport = async (data) => {
    const { work_unit_token, product_type_id } = data

    try {
      setLoading(true)
      const params = {
        work_unit_token,
        ...(product_type_id && { product_type_id }),
      }

      const fileContent = await routeConfigServices.export(params)

      // Verificar si hay errores en la respuesta
      if (typeof fileContent === 'object' && fileContent.errors && fileContent.errors.length > 0) {
        notify('ra.message.no_records_found', { type: 'error' })
        setLoading(false)
        return
      }

      const workUnit = workUnitOptions.find(wu => wu.token === work_unit_token)
      const workUnitName = workUnit?.long_name || work_unit_token
      const productType = product_type_id 
        ? productTypeOptions.find(pt => pt.id === product_type_id)
        : null
      const productTypeName = productType?.long_name || product_type_id

      // Crear timestamp en formato YYYYMMDD-HHMMSS en hora local
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      const seconds = String(now.getSeconds()).padStart(2, '0')
      const timestamp = `${year}${month}${day}-${hours}${minutes}${seconds}`

      // Limpiar nombres para usar en nombre de archivo (remover caracteres especiales)
      const cleanWorkUnitName = workUnitName.replace(/[^a-zA-Z0-9]/g, '_')
      const cleanProductTypeName = productTypeName ? productTypeName.replace(/[^a-zA-Z0-9]/g, '_') : ''

      // Crear blob con UTF-8 BOM para mejor compatibilidad con Excel
      const blob = new Blob(['\ufeff' + fileContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `route-config-${cleanWorkUnitName}${cleanProductTypeName ? `-${cleanProductTypeName}` : ''}-${timestamp}.csv`
      link.click()
      URL.revokeObjectURL(url)

      notify('ra.message.download_export_success', { type: 'success' })
      setExportLoading(true)
      setTimeout(() => setExportLoading(false), 10000)
      onCloseModal()
    } catch (error) {
      console.error('Export error:', error)
      notify('ra.message.export_error', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Button
        onClick={onOpenModal}
        disabled={loading || exportLoading}
        startIcon={loading || exportLoading ? <CircularProgress size={20} /> : <Download fontSize='small' />}
        size='small'
        sx={{ lineHeight: 1.5 }}
      >
        {translate('ra.action.export')}
      </Button>

      <Dialog open={openModal} onClose={onCloseModal} maxWidth='md' fullWidth>
        <DialogTitle display='flex' justifyContent='space-between' alignItems='center'>
          {translate('ra.title.route_config_export')}
          <IconButton size='small' onClick={onCloseModal}>
            <Close />
          </IconButton>
        </DialogTitle>
        <Form onSubmit={handleExport}>
          <DialogContent>
            <Grid container spacing={3}>
              <EditInputsWrap label={translate('ra.field.work_unit')} className='small' required>
                {loadingWorkUnits ? (
                  <Skeleton variant='rectangular' height={40} />
                ) : (
                  <SelectInput
                    source='work_unit_token'
                    options={workUnitOptions}
                    optionsText={['long_name']}
                    optionValue='token'
                    validate={[required()]}
                    size='small'
                    showSearch
                    limitOptions={false}
                  />
                )}
              </EditInputsWrap>

              <EditInputsWrap label={translate('ra.field.product_type')} className='small'>
                {loadingProductTypes ? (
                  <Skeleton variant='rectangular' height={40} />
                ) : (
                  <SelectInput
                    source='product_type_id'
                    options={productTypeOptions}
                    optionsText={['long_name']}
                    optionValue='id'
                    size='small'
                    showSearch
                    limitOptions={false}
                  />
                )}
              </EditInputsWrap>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={onCloseModal} variant='outlined'>
              {translate('ra.action.cancel')}
            </Button>
            <LoadingButton
              loading={loading}
              loadingPosition='start'
              startIcon={<Download />}
              variant='contained'
              type='submit'
              disabled={loading || loadingWorkUnits}
            >
              {translate('ra.action.export')}
            </LoadingButton>
          </DialogActions>
        </Form>
      </Dialog>
    </>
  )
}
